import React, { useEffect, useState } from 'react';
import { View, Text, FlatList, StyleSheet, Alert, Platform } from 'react-native';
import theme from '../../theme';
import { fetchRemoteTransactions, fetchLocalTransactions, deleteTransaction } from './transactionsRepository';
import { useI18n } from '../../i18n/useI18n';
import AppButton from '../../components/AppButton';

export default function TransactionsList({
  userId,
  lang,
  onEditTransaction,
}: {
  userId?: string;
  lang?: string;
  onEditTransaction?: (txn: any) => void;
}) {
  const [txns, setTxns] = useState<any[]>([]);
  const { t, locale } = useI18n(lang);

  async function confirmDelete(): Promise<boolean> {
    // Web: RN Alert may be a no-op depending on bundler/runtime.
    if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.confirm === 'function') {
      return window.confirm(`${t('confirmDeleteTitle') || 'Delete transaction?'}\n\n${t('confirmDeleteMessage') || 'This action cannot be undone.'}`);
    }

    return await new Promise<boolean>((resolve) => {
      Alert.alert(
        t('confirmDeleteTitle') || 'Delete transaction?',
        t('confirmDeleteMessage') || 'This action cannot be undone.',
        [
          { text: t('cancel') || 'Cancel', style: 'cancel', onPress: () => resolve(false) },
          { text: t('delete') || 'Delete', style: 'destructive', onPress: () => resolve(true) },
        ]
      );
    });
  }

  async function load() {
    // prefer remote when possible
    try {
      const remote = await fetchRemoteTransactions(userId);
      setTxns(remote || []);
    } catch (e) {
      const local = await fetchLocalTransactions();
      setTxns(local || []);
    }
  }

  useEffect(() => { load(); }, [userId]);

  async function handleDelete(id: string) {
    const ok = await confirmDelete();
    if (!ok) return;

    // Optimistic UI update
    setTxns((prev) => prev.filter((t) => t.id !== id));

    const deletedOnServer = await deleteTransaction(id);
    if (!deletedOnServer) {
      Alert.alert('Error', 'Could not delete on server.');
      // Re-sync list (item may reappear if server delete failed)
      load();
      return;
    }

    load();
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={txns}
        keyExtractor={(i) => i.id}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={<Text style={{ color: theme.colors.textSecondary }}>{t('noTransactions') || 'No transactions'}</Text>}
        renderItem={({ item }) => (
          <View style={[styles.row, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}> 
            <View style={{ flex: 1 }}>
              <Text style={[styles.amount, { color: theme.colors.textPrimary }]}>{item.type === 'expense' ? '-' : '+'}${Number(item.amount).toFixed(2)}</Text>
              <Text style={{ color: theme.colors.textSecondary }}>{item.category} • {formatDisplayDate(new Date(item.date), locale)}</Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <AppButton
                title={t('edit') || 'Edit'}
                onPress={() => {
                  onEditTransaction?.(item);
                }}
                tone="secondary"
                variant="outline"
                style={{ minHeight: 34, paddingVertical: 6, paddingHorizontal: 10 }}
                textStyle={{ fontSize: 12 }}
              />
              <View style={{ width: 8 }} />
              <AppButton
                title={t('delete') || 'Delete'}
                onPress={() => { void handleDelete(item.id); }}
                tone="error"
                style={{ minHeight: 34, paddingVertical: 6, paddingHorizontal: 10 }}
                textStyle={{ fontSize: 12 }}
              />
            </View>
          </View>
        )}
      />
    </View>
  );
}

function formatDisplayDate(d: Date | string | undefined, locale?: string) {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '';
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = dt.getFullYear();
  return locale === 'es' ? `${dd}/${mm}/${yyyy}` : `${mm}/${dd}/${yyyy}`;
}

const styles = StyleSheet.create({
  container: { flex: 1, width: '100%', maxWidth: 720 },
  list: { flex: 1 },
  listContent: { paddingBottom: 12 },
  row: { padding: 12, marginVertical: 6, marginHorizontal: 0, borderRadius: 8, borderWidth: 1, flexDirection: 'row', alignItems: 'center' },
  amount: { fontSize: 16, fontWeight: '700' }
});
